import { useEffect, useState } from "react";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { api } from "@/lib/api";

const GROUPS = [
  { title: "Glycaemic control", keys: ["hba1c"], ref: [{ y: 6.5, label: "Diabetes ≥6.5" }, { y: 5.7, label: "Prediabetes ≥5.7" }] },
  { title: "Fasting glucose", keys: ["fasting_glucose"], ref: [{ y: 126, label: "≥126" }, { y: 100, label: "≥100" }] },
  { title: "Lipid profile", keys: ["ldl", "hdl", "triglycerides", "total_cholesterol"], ref: [{ y: 130, label: "LDL ≥130" }] },
  { title: "Blood pressure", keys: ["systolic_bp", "diastolic_bp"], ref: [{ y: 140, label: "SBP ≥140" }, { y: 90, label: "DBP ≥90" }] },
  { title: "Vitamins & body", keys: ["vitamin_d", "vitamin_b12", "bmi", "hemoglobin"], ref: [] },
  { title: "Inflammation, thyroid, renal", keys: ["hs_crp", "tsh", "creatinine"], ref: [] },
];
const COLORS = ["#0D7C85", "#D97706", "#DC2626", "#2563EB", "#7C3AED"];

function Delta({ s }) {
  if (s.change == null) return <span className="text-xs text-slate-400 flex items-center gap-1"><Minus className="w-3 h-3" /> single reading</span>;
  const up = s.change > 0;
  const good = ["hdl", "vitamin_d", "vitamin_b12", "hemoglobin"].includes(s.key) ? up : !up;
  return <span className={`text-xs font-semibold flex items-center gap-1 ${good ? "text-emerald-700" : "text-red-700"}`} data-testid={`trend-delta-${s.key}`}>{up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : ""}{s.change} {s.unit}</span>;
}

function Chart({ group, series }) {
  const present = group.keys.map((k) => series.find((s) => s.key === k)).filter(Boolean);
  if (!present.length) return null;
  const dates = [...new Set(present.flatMap((s) => s.points.map((p) => p.date)))].sort();
  const data = dates.map((d) => Object.fromEntries([["date", d], ...present.map((s) => [s.key, s.points.find((p) => p.date === d)?.value ?? null])]));
  return (
    <div className="clinical-card p-5" data-testid={`trend-chart-${group.keys[0]}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <h3 className="font-heading font-semibold text-slate-900">{group.title}</h3>
        <div className="flex flex-wrap gap-3">{present.map((s, i) => <div key={s.key} className="text-right"><p className="text-[11px] font-medium" style={{ color: COLORS[i] }}>{s.label} · {s.latest} {s.unit}</p><Delta s={s} /></div>)}</div>
      </div>
      <div className="h-56 min-w-0" style={{ minHeight: 224 }}>
        <ResponsiveContainer width="100%" height={224} minWidth={200}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { month: "short", year: "2-digit" })} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
            {group.ref.map((r) => <ReferenceLine key={r.y} y={r.y} stroke="#DC2626" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: r.label, fontSize: 10, fill: "#991B1B", position: "insideTopRight" }} />)}
            {present.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={COLORS[i]} strokeWidth={2.5} dot={{ r: 4 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function TrendsTab({ patient }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/patients/${patient.id}/trends`).then((r) => setData(r.data)).catch(() => setData({ series: [], visits: [] })); }, [patient.id]);
  if (!data) return <p className="text-sm text-slate-500">Loading trends…</p>;
  if (!data.series.length) return <div className="clinical-card p-8 text-center text-sm text-slate-500" data-testid="trends-empty">No metrics recorded yet. Add health check data to see trends.</div>;
  return (
    <div className="space-y-4" data-testid="health-trends-view">
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600"><span className="font-semibold text-slate-900">{data.visits.length} visit{data.visits.length !== 1 ? "s" : ""}</span> · {data.visits[0]} → {data.visits[data.visits.length - 1]} · dashed lines mark India-guideline thresholds</div>
      <div className="grid lg:grid-cols-2 gap-4">{GROUPS.map((g) => <Chart key={g.title} group={g} series={data.series} />)}</div>
    </div>
  );
}
